import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Payment,
  Description,
  TrendingUp,
  Security,
  Speed,
} from '@mui/icons-material';

/**
 * Paywall Modal Component
 * Shown when user needs to subscribe or renew
 */
const PaywallModal = ({ 
  open, 
  onClose, 
  onSubscribe, 
  reason = 'limit_exceeded', 
  subscription = null,
  config = { price: 10, currency: 'USD', pagesPerMonth: 100, trialDays: 7 }
}) => {
  const features = [
    {
      icon: <Description />,
      title: `${config.pagesPerMonth} Pages Per Month`,
      description: `Process up to ${config.pagesPerMonth} PDF pages every month`,
    },
    {
      icon: <TrendingUp />,
      title: 'AI-Powered Analysis',
      description: 'Automatic transaction categorization',
    },
    {
      icon: <Speed />,
      title: 'Instant Processing',
      description: 'Fast file upload and analysis',
    },
    {
      icon: <Security />,
      title: 'Secure & Private',
      description: 'Your data is encrypted and protected',
    },
  ];

  const formatPages = (pages) => {
    if (typeof pages === 'number') {
      return pages.toFixed(2);
    }
    return parseFloat(pages || 0).toFixed(2);
  };

  const getMessage = () => {
    switch (reason) {
      case 'limit_exceeded':
        return {
          title: 'Monthly Limit Reached',
          description: `You've used all ${formatPages(subscription?.pagesUsed || 0)} of your ${subscription?.maxPages || config.pagesPerMonth} pages this month.`,
          action: 'Subscribe to continue',
        };
      case 'subscription_expired':
        return {
          title: 'Subscription Expired',
          description: 'Your subscription has ended. Renew to continue uploading files.',
          action: 'Renew Subscription',
        };
      case 'trial_ended':
        return {
          title: 'Trial Period Ended',
          description: `Your ${config.trialDays}-day trial has ended. Subscribe to continue using PinePocket.`,
          action: 'Subscribe Now',
        };
      default:
        return {
          title: 'Subscription Required',
          description: 'Subscribe to unlock all features and continue using PinePocket.',
          action: 'Subscribe',
        };
    }
  };

  const message = getMessage();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 3,
      }}
    >
      {/* Header */}
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            {message.title}
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            color="inherit"
            startIcon={<Close />}
          >
            Close
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Main Message */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1">{message.description}</Typography>
          </Alert>

          {/* Pricing */}
          <Box
            sx={{
              p: 3,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h3" fontWeight="bold">
              ${config.price.toFixed(2)}
            </Typography>
            <Typography variant="h6">per month</Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Cancel anytime • No hidden fees
            </Typography>
          </Box>

          <Divider />

          {/* Features */}
          <Box>
            <Typography variant="h6" gutterBottom fontWeight="medium">
              What's Included
            </Typography>
            <List dense>
              {features.map((feature, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 40, color: 'success.main' }}>
                    {feature.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={feature.title}
                    secondary={feature.description}
                    primaryTypographyProps={{ fontWeight: 'medium' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Usage Info (if available) */}
          {subscription && subscription.pagesUsed !== undefined && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Usage
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatPages(subscription.pagesUsed)} / {subscription.maxPages} pages
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Your usage resets monthly with each subscription renewal
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Stack spacing={2} width="100%">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Payment />}
            onClick={onSubscribe}
            fullWidth
          >
            {message.action}
          </Button>
          <Button variant="text" onClick={onClose} fullWidth>
            Maybe Later
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PaywallModal;

